
import React, { useEffect, useMemo, useState } from "react";
import {
  Search,
  ClipboardList,
  Clock3,
  CheckCircle2,
  UserCheck,
  RefreshCw,
  MapPin,
  Building2,
  CalendarDays,
} from "lucide-react";
import "./RequestList.css";

const API_URL = "/api/job-applications";

const PAGE_CONFIG = {
  pending: {
    title: "Pending Requests",
    description: "View trainer applications awaiting further processing.",
    statuses: ["PENDING"],
    icon: Clock3,
  },
  approved: {
    title: "Approved Requests",
    description: "View shortlisted trainers and trainers in the interview stage.",
    statuses: ["SHORTLISTED", "INTERVIEW"],
    icon: CheckCircle2,
  },
  selected: {
    title: "Final Selected Trainers",
    description: "View trainers whose applications have been marked as selected.",
    statuses: ["SELECTED"],
    icon: UserCheck,
  },
};

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getStatusClass = (status) =>
  String(status || "UNKNOWN").toLowerCase().replace(/[^a-z0-9]+/g, "-");

function RequestList({ type = "pending" }) {
  const config = PAGE_CONFIG[type] || PAGE_CONFIG.pending;
  const PageIcon = config.icon;

  const [applications, setApplications] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadApplications = async () => {
    setLoading(true);
    setError("");

    try {
      const response = await fetch(API_URL);

      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
      }

      const data = await response.json();

      if (!Array.isArray(data)) {
        throw new Error("The applications API did not return a list.");
      }

      // Keep this page read-only. Never call status-update endpoints here.
      // Rejected and withdrawn applications are intentionally excluded.
      const visibleApplications = data.filter((item) => {
        const status = String(item?.applicationStatus || "")
          .trim()
          .toUpperCase();

        return (
          config.statuses.includes(status) &&
          status !== "REJECTED" &&
          status !== "WITHDRAWN" &&
          item?.isActive !== false
        );
      });

      setApplications(visibleApplications);
    } catch (err) {
      setError(
        err.message ||
          "Unable to load applications. Check that the Spring Boot backend is running."
      );
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadApplications();
    // The route's type determines which statuses this page displays.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const filteredApplications = useMemo(() => {
    const query = search.trim().toLowerCase();

    return applications.filter((item) => {
      const status = String(item.applicationStatus || "").toUpperCase();

      const matchesStatus =
        statusFilter === "ALL" || status === statusFilter;

      const searchableText = [
        item.applicationNumber,
        item.applicationId,
        item.jobTitle,
        item.jobLocation,
        item.companyName,
        item.trainerName,
        item.trainerEmail,
        item.trainerPhone,
        item.applicationStatus,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return matchesStatus && searchableText.includes(query);
    });
  }, [applications, search, statusFilter]);

  const availableStatuses = config.statuses;

  return (
    <main className="request-page">
      <section className="request-page-header">
        <div className="request-heading">
          <div className="request-heading-icon">
            <PageIcon size={25} />
          </div>

          <div>
            <h1>{config.title}</h1>
            <p>{config.description}</p>
          </div>
        </div>

        <button
          type="button"
          className="request-refresh-button"
          onClick={loadApplications}
          disabled={loading}
        >
          <RefreshCw size={16} className={loading ? "request-spinning" : ""} />
          Refresh
        </button>
      </section>

      <section className="request-summary-grid">
        <div className="request-summary-card">
          <div className="request-summary-icon">
            <ClipboardList size={21} />
          </div>
          <div>
            <span>Total Requests</span>
            <strong>{applications.length}</strong>
          </div>
        </div>

        <div className="request-summary-card">
          <div className="request-summary-icon">
            <Search size={21} />
          </div>
          <div>
            <span>Matching Results</span>
            <strong>{filteredApplications.length}</strong>
          </div>
        </div>
      </section>

      <section className="request-table-card">
        <div className="request-toolbar">
          <div className="request-search">
            <Search size={18} />
            <input
              type="search"
              placeholder="Search trainer, company, job..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              aria-label="Search requests"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
            aria-label="Filter by application status"
          >
            <option value="ALL">All displayed statuses</option>
            {availableStatuses.map((status) => (
              <option key={status} value={status}>
                {status.replaceAll("_", " ")}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <div className="request-state">
            <RefreshCw size={25} className="request-spinning" />
            <p>Loading requests...</p>
          </div>
        ) : error ? (
          <div className="request-state request-error">
            <ClipboardList size={28} />
            <h3>Unable to load requests</h3>
            <p>{error}</p>
            <button type="button" onClick={loadApplications}>
              Try again
            </button>
          </div>
        ) : filteredApplications.length === 0 ? (
          <div className="request-state">
            <ClipboardList size={30} />
            <h3>No requests found</h3>
            <p>
              {search || statusFilter !== "ALL"
                ? "Try changing your search or status filter."
                : "There are currently no requests in this section."}
            </p>
          </div>
        ) : (
          <div className="request-table-scroll">
            <table className="request-table">
              <thead>
                <tr>
                  <th>Application</th>
                  <th>Trainer</th>
                  <th>Job Details</th>
                  <th>Partner / Company</th>
                  <th>Applied On</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {filteredApplications.map((item) => {
                  const status = String(
                    item.applicationStatus || "UNKNOWN"
                  ).toUpperCase();

                  return (
                    <tr key={item.applicationId ?? item.applicationNumber}>
                      <td>
                        <strong className="request-application-number">
                          {item.applicationNumber ||
                            `APP-${item.applicationId ?? "—"}`}
                        </strong>
                        <span className="request-secondary-text">
                          ID: {item.applicationId ?? "—"}
                        </span>
                      </td>

                      <td>
                        <strong>{item.trainerName || "—"}</strong>
                        <span className="request-secondary-text">
                          {item.trainerEmail || "No email"}
                        </span>
                        {item.trainerPhone && (
                          <span className="request-secondary-text">
                            {item.trainerPhone}
                          </span>
                        )}
                      </td>

                      <td>
                        <strong>{item.jobTitle || "Job title unavailable"}</strong>
                        <span className="request-secondary-text request-inline-detail">
                          <MapPin size={13} />
                          {item.jobLocation || "Location not specified"}
                        </span>
                        <span className="request-secondary-text">
                          Job ID: {item.jobId ?? "—"}
                        </span>
                      </td>

                      <td>
                        <span className="request-company-name">
                          <Building2 size={14} />
                          {item.companyName || "—"}
                        </span>
                        <span className="request-secondary-text">
                          Partner ID: {item.partnerId ?? "—"}
                        </span>
                      </td>

                      <td>
                        <span className="request-inline-detail">
                          <CalendarDays size={14} />
                          {formatDate(item.appliedAt)}
                        </span>
                      </td>

                      <td>
                        <span
                          className={`request-status status-${getStatusClass(status)}`}
                        >
                          {status.replaceAll("_", " ")}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && !error && filteredApplications.length > 0 && (
          <div className="request-table-footer">
            Showing {filteredApplications.length} of {applications.length} requests
          </div>
        )}
      </section>

      <p className="request-readonly-note">
        <CheckCircle2 size={15} />
        Admin view only — this page does not modify application statuses.
      </p>
    </main>
  );
}

export default RequestList;
